import { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Button, Form, Input } from 'semantic-ui-react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import qs from 'query-string';

import { Helmet } from '@plone/volto/helpers/Helmet/Helmet';
import { usePrevious } from '@plone/volto/helpers/Utils/usePrevious';
import config from '@plone/volto/registry';
import {
  login,
  logout,
  resetLoginRequest,
} from '@plone/volto/actions/userSession/userSession';
import { purgeMessages } from '@plone/volto/actions/messages/messages';

import { toast } from 'react-toastify';
import { default as Toast } from '@plone/volto/components/manage/Toast/Toast';
import { default as Icon } from '@plone/volto/components/theme/Icon/Icon';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import arrowRight from '../../theme/icons/arrow-right.svg';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';

const messages = defineMessages({
  login: {
    id: 'Log in',
    defaultMessage: 'Log in',
  },
  loginName: {
    id: 'Login Name',
    defaultMessage: 'Login Name',
  },
  Login: {
    id: 'Login',
    defaultMessage: 'Login',
  },
  password: {
    id: 'Password',
    defaultMessage: 'Password',
  },
  cancel: {
    id: 'Cancel',
    defaultMessage: 'Cancel',
  },
  error: {
    id: 'Error',
    defaultMessage: 'Error',
  },
  loginFailed: {
    id: 'Login Failed',
    defaultMessage: 'Login Failed',
  },
  loginFailedContent: {
    id: 'Both email address and password are case sensitive, check that caps lock is not enabled.',
    defaultMessage:
      'Both email address and password are case sensitive, check that caps lock is not enabled.',
  },
  register: {
    id: 'Register',
    defaultMessage: 'Register',
  },
  forgotPassword: {
    id: 'box_forgot_password_option',
    defaultMessage: 'Forgot your password?',
  },
});

const Login = (props) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userSession.token, shallowEqual);
  const error = useSelector((state) => state.userSession.login.error);
  const loading = useSelector((state) => state.userSession.login.loading);
  const locale = useSelector((state) => state.intl.locale);
  const [type, setType] = useState(true);
  const returnUrl =
    qs.parse(props.location?.search ?? location.search).return_url ||
    location.pathname.replace(/\/[^/]*\/?$/, '') ||
    '/';
  const previousToken = usePrevious(token);

  useEffect(() => {
    if (location?.state?.isLogout) {
      // Execute a true Logout action
      // This is needed to cover the use case of being logged in in
      // another backend (eg. in development), having a token for
      // localhost and try to use it, the login route has to know that
      // it's the same as it comes from a logout
      // See also Unauthorized.jsx
      dispatch(logout());
      dispatch(purgeMessages());
      // Reset the location state
      history.push(`${location.pathname}${location.search}`);
    } else if (token && token !== previousToken) {
      // We just did a true login action
      history.push(returnUrl || '/');
      if (toast.isActive('loggedOut')) {
        toast.dismiss('loggedOut');
      }
      if (toast.isActive('loginFailed')) {
        toast.dismiss('loginFailed');
      }
    }
    if (error) {
      if (toast.isActive('loggedOut')) {
        toast.dismiss('loggedOut');
      }
      if (!toast.isActive('loginFailed')) {
        toast.error(
          <Toast
            error
            title={intl.formatMessage(messages.loginFailed)}
            content={intl.formatMessage(messages.loginFailedContent)}
          />,
          { autoClose: false, toastId: 'loginFailed' },
        );
      }
    }
    return () => {
      if (toast.isActive('loginFailed')) {
        toast.dismiss('loginFailed');
        dispatch(resetLoginRequest());
      }
    };
  }, [
    dispatch,
    token,
    error,
    intl,
    history,
    returnUrl,
    location.search,
    location.pathname,
    location?.state?.isLogout,
    previousToken,
  ]);

  const onLogin = (event) => {
    dispatch(
      login(
        document.getElementsByName('login')[0].value,
        document.getElementsByName('password')[0].value,
      ),
    );
    event.preventDefault();
  };

  return (
    <div id="page-login">
      <Helmet title={intl.formatMessage(messages.Login)} />
      <div className="login-container">
        {' '}
        <h1>
          <FormattedMessage id="Log In" defaultMessage="Login" />
        </h1>
        <Form method="post" onSubmit={onLogin} className="ui form">
          <Form.Field inline className="help wide">
            <div className="wrapper">
              <label htmlFor="login">
                <FormattedMessage id="Login Name" defaultMessage="Login Name" />
              </label>
            </div>
            <Input
              id="login"
              name="login"
              placeholder={intl.formatMessage(messages.loginName)}
            />
          </Form.Field>
          <Form.Field inline className="help wide">
            <div className="wrapper">
              <label htmlFor="password">
                <FormattedMessage id="Password" defaultMessage="Password" />
              </label>
            </div>
            <Input
              type={type ? 'password' : 'text'}
              id="password"
              autoComplete="current-password"
              name="password"
              placeholder={intl.formatMessage(messages.password)}
              tabIndex={0}
              icon={
                type ? (
                  <FontAwesomeIcon
                    icon={faEye}
                    onClick={() => setType(!type)}
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faEyeSlash}
                    onClick={() => setType(!type)}
                  />
                )
              }
            />
          </Form.Field>
          <Form.Field inline className="help">
            {config.settings.showSelfRegistration && (
              <p className="help">
                <Link to={`/${locale}/register`}>
                  {intl.formatMessage(messages.register)}
                </Link>
              </p>
            )}
            <p className="help">
              <Link to={`/${locale}/passwordreset`}>
                {intl.formatMessage(messages.forgotPassword)}
              </Link>
            </p>
          </Form.Field>
          <Button
            primary
            type="submit"
            id="login-form-submit"
            aria-label={intl.formatMessage(messages.login)}
            title={intl.formatMessage(messages.login)}
            loading={loading}
          >
            <FormattedMessage id="Log in (action)" defaultMessage="Log in" />{' '}
            <Icon
              className="arrow svg-inline--fa"
              name={arrowRight}
              style={{ width: '12px', height: '12px' }}
              title="arrow"
            />{' '}
          </Button>
        </Form>
      </div>
    </div>
  );
};

export default Login;
